import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const PaymentSettings = () => {
  const [settings, setSettings] = useState({
    mode: 'sdk',
    customerOrderMode: 'webview',
    allowModeSwitch: true,
    codEnabled: true,
    codMaxOrderValue: 500,
    webviewCallbackUrl: '',
    digitalOnDeliveryEnabled: false,
    lastUpdated: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const theme = {
    primaryColor: '#FF6961',
    primaryDark: '#E6524A',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    backgroundColor: '#F8FAFC'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getPaymentSettings();
      const data = response.data?.data || response.data || {};
      setSettings({
        mode: data.mode || 'sdk',
        customerOrderMode: data.customerOrderMode || 'webview',
        allowModeSwitch: data.allowModeSwitch ?? true,
        codEnabled: data.codEnabled ?? true,
        codMaxOrderValue: data.codMaxOrderValue ?? 500,
        webviewCallbackUrl: data.webviewCallbackUrl || '',
        digitalOnDeliveryEnabled: data.digitalOnDeliveryEnabled ?? false,
        lastUpdated: data.lastUpdated
      });
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load payment settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await settingsAPI.updatePaymentSettings({
        mode: settings.mode,
        customerOrderMode: settings.customerOrderMode,
        allowModeSwitch: settings.allowModeSwitch,
        codEnabled: settings.codEnabled,
        codMaxOrderValue: Number(settings.codMaxOrderValue) || 0,
        webviewCallbackUrl: settings.webviewCallbackUrl,
        digitalOnDeliveryEnabled: settings.digitalOnDeliveryEnabled
      });

      setMessage({
        type: 'success',
        text: 'Payment settings updated successfully!'
      });

      // Refresh to get updated timestamp
      fetchSettings();
    } catch (error) {
      console.error('Error saving payment settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save payment settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = (newMode) => {
    setSettings(prev => ({ ...prev, mode: newMode }));
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{
          minHeight: '100vh',
          backgroundColor: theme.backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: `3px solid ${theme.primaryColor}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p style={{ color: theme.textSecondary }}>Loading payment settings...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.backgroundColor,
        padding: '24px'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '32px' }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: theme.textPrimary,
              marginBottom: '8px'
            }}>
              Payment Gateway Settings
            </h1>
            <p style={{ color: theme.textSecondary }}>
              Configure how payment is processed in mobile apps
            </p>
          </div>

          {/* Message */}
          {message.text && (
            <div style={{
              padding: '16px',
              marginBottom: '24px',
              borderRadius: '12px',
              backgroundColor: message.type === 'success' ? '#DCFCE7' : '#FEE2E2',
              color: message.type === 'success' ? '#166534' : '#DC2626',
              border: `1px solid ${message.type === 'success' ? '#86EFAC' : '#FECACA'}`
            }}>
              {message.text}
            </div>
          )}

          {/* Main Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {/* Cash on Delivery availability (customer checkout) */}
            <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '8px' }}>
                Cash on Delivery (COD)
              </h2>
              <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '16px' }}>
                Whether customers can choose <strong>Cash on Delivery</strong> at checkout in the user app. When disabled, COD is hidden in the app <strong>and</strong> any COD order is rejected by the server — customers must pay online.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[{ v: true, label: '✅ COD Enabled' }, { v: false, label: '🚫 COD Disabled' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => setSettings(prev => ({ ...prev, codEnabled: opt.v }))}
                    style={{
                      flex: 1,
                      padding: '14px',
                      borderRadius: '10px',
                      border: `2px solid ${settings.codEnabled === opt.v ? theme.primaryColor : '#E2E8F0'}`,
                      backgroundColor: settings.codEnabled === opt.v ? '#FFF5F5' : 'white',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: settings.codEnabled === opt.v ? theme.primaryDark : theme.textSecondary
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p style={{ color: '#92400E', fontSize: '12px', marginTop: '10px' }}>
                Current: <strong>{settings.codEnabled ? 'Cash on Delivery is available to customers' : 'Cash on Delivery is OFF — online payment only'}</strong>. Click “Save Changes” below to apply. Reversible anytime.
              </p>

              {/* COD maximum order value — above this grand total, COD is blocked at checkout */}
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #E2E8F0', opacity: settings.codEnabled ? 1 : 0.5 }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: theme.textPrimary, display: 'block', marginBottom: '6px' }}>
                  COD maximum order value (₹)
                </label>
                <p style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '12px' }}>
                  Orders whose <strong>grand total</strong> (final amount the customer pays, incl. delivery &amp; tax) is <strong>above this</strong> cannot use Cash on Delivery — the app disables COD and the server rejects it. Set to <strong>0</strong> for no limit.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: theme.textSecondary, fontSize: '15px' }}>₹</span>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={settings.codMaxOrderValue}
                    onChange={(e) => setSettings(prev => ({ ...prev, codMaxOrderValue: e.target.value === '' ? '' : Number(e.target.value) }))}
                    disabled={!settings.codEnabled}
                    style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '14px', width: '160px', boxSizing: 'border-box' }}
                    placeholder="500"
                  />
                </div>
                <p style={{ color: '#92400E', fontSize: '12px', marginTop: '10px' }}>
                  {Number(settings.codMaxOrderValue) > 0
                    ? <>COD allowed only up to <strong>₹{Number(settings.codMaxOrderValue)}</strong>. Orders above this must pay online.</>
                    : <>No COD limit — Cash on Delivery allowed at any order value.</>}
                </p>
              </div>
            </div>

            {/* Digital-on-Delivery: UPI at the door on COD orders (a choice; cash stays) */}
            <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '8px' }}>
                Digital-on-Delivery (UPI at the door)
              </h2>
              <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '16px' }}>
                When ON, COD orders can be paid by <strong>UPI at delivery</strong> — the customer scans a QR / taps a link and the money goes straight to FoodWinc, so the rider never handles cash. It's a <strong>choice</strong>: cash stays a fallback and no delivery is ever blocked. (Great for 3rd-party riders.)
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[{ v: true, label: 'ON' }, { v: false, label: 'OFF' }].map(opt => (
                  <button
                    key={String(opt.v)}
                    onClick={() => setSettings(prev => ({ ...prev, digitalOnDeliveryEnabled: opt.v }))}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600',
                      border: `2px solid ${settings.digitalOnDeliveryEnabled === opt.v ? theme.primaryColor : '#E2E8F0'}`,
                      backgroundColor: settings.digitalOnDeliveryEnabled === opt.v ? '#FFF5F5' : 'white',
                      color: settings.digitalOnDeliveryEnabled === opt.v ? theme.primaryDark : theme.textSecondary
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p style={{ color: theme.textSecondary, fontSize: '13px', marginTop: '10px' }}>
                Current: <strong>{settings.digitalOnDeliveryEnabled ? 'Riders & customers can pay COD by UPI at the door' : 'OFF — COD is cash only'}</strong>. Requires <strong>foodwinc.com</strong> whitelisted on Cashfree. Reversible anytime.
              </p>
            </div>

            {/* Customer App — Order Payments (independent per-app knob) */}
            <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: theme.textPrimary, marginBottom: '8px' }}>
                Customer App — Order Payments
              </h2>
              <p style={{ color: theme.textSecondary, fontSize: '14px', marginBottom: '16px' }}>
                How the <strong>customer app</strong> processes online order payments. <strong>Native SDK</strong> shows one-tap UPI apps (GPay / PhonePe / Paytm); <strong>WebView</strong> does not. (The customer app package is whitelisted with Cashfree, so SDK is safe here.)
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {['sdk', 'webview'].map(opt => (
                  <button
                    key={opt}
                    onClick={() => setSettings(prev => ({ ...prev, customerOrderMode: opt }))}
                    style={{
                      flex: 1,
                      padding: '14px',
                      borderRadius: '10px',
                      border: `2px solid ${settings.customerOrderMode === opt ? theme.primaryColor : '#E2E8F0'}`,
                      backgroundColor: settings.customerOrderMode === opt ? '#FFF5F5' : 'white',
                      cursor: 'pointer',
                      fontWeight: '600',
                      color: settings.customerOrderMode === opt ? theme.primaryDark : theme.textSecondary
                    }}
                  >
                    {opt === 'sdk' ? '🟢 Native SDK (UPI apps)' : '🌐 WebView'}
                  </button>
                ))}
              </div>
              <p style={{ color: '#92400E', fontSize: '12px', marginTop: '10px' }}>
                Current: <strong>{settings.customerOrderMode === 'sdk' ? 'Native SDK — customers see UPI apps' : 'WebView'}</strong>. Click “Save” below to apply. Reversible anytime.
              </p>
            </div>

            {/* Payment Mode Section — Restaurant & Delivery apps */}
            <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.textPrimary,
                marginBottom: '8px'
              }}>
                Restaurant &amp; Delivery Apps — Payment Mode
              </h2>
              <p style={{
                color: theme.textSecondary,
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Applies to restaurant subscription/addon payments and delivery onboarding/COD deposits. ⚠️ Switch to Native SDK <strong>only after</strong> whitelisting the restaurant &amp; delivery app packages in Cashfree — otherwise those payments will fail.
              </p>

              {/* Mode Options */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* SDK Mode */}
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '20px',
                  borderRadius: '12px',
                  border: `2px solid ${settings.mode === 'sdk' ? theme.primaryColor : '#E2E8F0'}`,
                  backgroundColor: settings.mode === 'sdk' ? '#FFF5F5' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="paymentMode"
                    value="sdk"
                    checked={settings.mode === 'sdk'}
                    onChange={() => handleModeChange('sdk')}
                    style={{
                      marginRight: '16px',
                      marginTop: '4px',
                      accentColor: theme.primaryColor
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: theme.textPrimary
                      }}>
                        Native SDK Mode
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#FEF3C7',
                        color: '#D97706',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        REQUIRES PACKAGE WHITELISTING
                      </span>
                    </div>
                    <p style={{
                      color: theme.textSecondary,
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      Uses Cashfree's native Flutter SDK for payment processing.
                      This mode requires your app package names to be whitelisted
                      in Cashfree dashboard (com.foodwinc.foodwincUser, etc.)
                    </p>
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: theme.textSecondary
                      }}>
                        <strong>Best for:</strong> Production apps with package name whitelisted
                      </span>
                    </div>
                  </div>
                </label>

                {/* WebView Mode */}
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '20px',
                  borderRadius: '12px',
                  border: `2px solid ${settings.mode === 'webview' ? theme.primaryColor : '#E2E8F0'}`,
                  backgroundColor: settings.mode === 'webview' ? '#FFF5F5' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="paymentMode"
                    value="webview"
                    checked={settings.mode === 'webview'}
                    onChange={() => handleModeChange('webview')}
                    style={{
                      marginRight: '16px',
                      marginTop: '4px',
                      accentColor: theme.primaryColor
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: theme.textPrimary
                      }}>
                        WebView Mode
                      </span>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#DCFCE7',
                        color: '#166534',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        DOMAIN WHITELISTING
                      </span>
                    </div>
                    <p style={{
                      color: theme.textSecondary,
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      Opens payment in a WebView using your whitelisted domain (foodwinc.com).
                      This mode works without package name whitelisting in Cashfree dashboard.
                    </p>
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#F8FAFC',
                      borderRadius: '8px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: theme.textSecondary
                      }}>
                        <strong>Best for:</strong> When domain is whitelisted but package names are not
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* How It Works Section */}
            <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: theme.textPrimary,
                marginBottom: '16px'
              }}>
                How It Works
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                {/* SDK Flow */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px'
                }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.textPrimary,
                    marginBottom: '12px'
                  }}>
                    SDK Mode Flow
                  </h3>
                  <ol style={{
                    margin: 0,
                    paddingLeft: '20px',
                    color: theme.textSecondary,
                    fontSize: '13px',
                    lineHeight: '1.8'
                  }}>
                    <li>App creates payment session via API</li>
                    <li>Native Cashfree SDK opens payment UI</li>
                    <li>User completes payment</li>
                    <li>SDK callback returns result</li>
                    <li>App verifies payment via backend</li>
                  </ol>
                </div>

                {/* WebView Flow */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px'
                }}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: theme.textPrimary,
                    marginBottom: '12px'
                  }}>
                    WebView Mode Flow
                  </h3>
                  <ol style={{
                    margin: 0,
                    paddingLeft: '20px',
                    color: theme.textSecondary,
                    fontSize: '13px',
                    lineHeight: '1.8'
                  }}>
                    <li>App creates payment session via API</li>
                    <li>App opens WebView with gateway URL</li>
                    <li>User completes payment in WebView</li>
                    <li>Redirect URL triggers app callback</li>
                    <li>App verifies payment via backend</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Status Section */}
            <div style={{ padding: '24px', backgroundColor: '#F8FAFC' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <p style={{
                    fontSize: '13px',
                    color: theme.textSecondary,
                    marginBottom: '4px'
                  }}>
                    Current Mode
                  </p>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: theme.textPrimary
                  }}>
                    {settings.mode === 'sdk' ? 'Native SDK' : 'WebView'} Mode
                  </p>
                  {settings.lastUpdated && (
                    <p style={{
                      fontSize: '12px',
                      color: theme.textSecondary,
                      marginTop: '4px'
                    }}>
                      Last updated: {new Date(settings.lastUpdated).toLocaleString()}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: saving ? '#CBD5E1' : theme.primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>

          {/* Warning Card */}
          <div style={{
            marginTop: '24px',
            padding: '20px',
            backgroundColor: '#FEF3C7',
            borderRadius: '12px',
            border: '1px solid #FCD34D'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>Warning</span>
              <div>
                <p style={{
                  fontWeight: '600',
                  color: '#92400E',
                  marginBottom: '8px'
                }}>
                  Changing payment mode affects all mobile apps
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#A16207',
                  lineHeight: '1.5'
                }}>
                  When you change the payment mode, all User and Restaurant mobile apps
                  will immediately start using the new payment flow. Make sure to test
                  thoroughly before switching in production.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSettings;
